// ── مراقبة الأخطاء (Error Monitoring / Sentry) ─────────────────────────────────
//
// حساب Sentry واحد يملكه مالك المنصة. أخطاء كل الشركات تتجمّع فيه مركزياً،
// وكل خطأ يُوسَم بـ companyId/role حتى يقدر مالك المنصة يفلتر "أي شركة" حصل عندها.
//
// كل شيء هنا مبوّب خلف متغير البيئة REACT_APP_SENTRY_DSN:
//   • لو لم يُضبط الـ DSN → كل الدوال تتحوّل لـ no-op (صفر تأثير، صفر كسر، لا شبكة).
//   • لتفعيلها: أنشئ مشروع Sentry (حساب مالك المنصة) وأضف REACT_APP_SENTRY_DSN
//     في متغيرات البيئة على Vercel، ثم أعد النشر.
//
// ⚠️ الحزمة تُحمَّل **ديناميكياً** (import داخل _loadSentry) لا استيراداً ثابتاً.
// السبب: هذا الملف يستورده src/index.js، فالاستيراد الثابت كان يضع Sentry داخل
// main.js — أي على المسار الحرج لكل **زائر متجر** أيضاً، وهو لا يستفيد منها.
// الحارس `if (!DSN) return` كان يمنع التشغيل لا التنزيل ولا التحليل (parse).
//
// المقاس فعلياً على البناء: main.js نزل من 176KB إلى 170KB مضغوطة (‎−6KB‎ عن كل
// زائر، أدمن كان أو زبون)، و Sentry صارت شنكاً مستقلاً 28KB لا يُجلب إطلاقاً بلا
// DSN، ولا يُجلب مع DSN إلا **عند خمول المتصفح** — خارج مسار الرسم بالكامل.
//
// تغطية الأخطاء المبكّرة (قبل وصول الحزمة) محفوظة: نركّب مستمعَين خفيفين فوراً
// (error / unhandledrejection) يخزّنان ما يقع في تلك النافذة، ونعيد بثّه إلى
// Sentry فور جاهزيته ثم نفكّهما — فلا يضيع خطأ ولا يُحتسب مرّتين.

import React from "react";

const DSN = process.env.REACT_APP_SENTRY_DSN || "";
let _enabled = false;
let _S = null;              // وحدة Sentry بعد تحميلها (null = لم تصل بعد)
let _pendingUser = null;    // آخر مستخدم طُلب وسمه قبل الجاهزية
const _pendingErrors = [];  // أخطاء وقعت قبل الجاهزية (مسقوفة كي لا تنمو بلا حدّ)
const PENDING_MAX = 20;

// ── ضجيج معروف: أخطاء لا تمثّل خللاً في التطبيق ولا يراها المستخدم ────────────
//
// التطبيق الأصلي (mobile/ — Capacitor) يحمّل الموقع اللايف مباشرة عبر
// `server.url` في capacitor.config.json، فيَحقن جسره الأصلي داخل صفحتنا.
// هذا الجسر — لا كودنا — هو مصدر هذي الأخطاء:
//   • iOS:     `window.webkit.messageHandlers` غير معرَّف في سياق التنفيذ.
//   • Android: كائن الـ @JavascriptInterface انمسح بالـ GC بعد تدمير الـ WebView
//              (التطبيق راح للخلفية بينما JS لسه شغّال) → "Java object is gone"
//              أو "Java exception was raised during method invocation".
// ولا سطر في src/ يستدعي هذي الجسور، وحزمة الويب لا تعتمد @capacitor/core أصلاً.
//
// AbortError = طلب شبكة أو تشغيل صوت انقطع طبيعياً (المستخدم غادر الصفحة أو
// انتهت المهلة). المواضع المهمة تعالجه أصلاً وتُظهر رسالة واضحة للمستخدم.
//
// "UnknownError: Database deleted by request of the user" = رسالة Blink (كروم/
// أندرويد WebView) حين تُحذف قاعدة IndexedDB بينما لنا اتصال مفتوح عليها، فيُغلق
// الاتصال قسراً ويُرفض ما كان جارياً. سببها خارج التطبيق بالكامل: المستخدم مسح
// بيانات الموقع/بيانات التطبيق، أو أجلى المتصفحُ التخزينَ تحت ضغط المساحة.
// وليس في `src/` سطر واحد يفتح IndexedDB بنفسه — الفاتحون كلهم داخل حزم Firebase
// (`firebase-heartbeat-database` من firebase/app، و installations/messaging على
// شاشات الأدمن، وفحص `indexedDBLocalPersistence` في مصادقة `src/firebase.js`)،
// وهم يعيدون فتحها تلقائياً في التحميل التالي. لا مستخدم يرى شيئاً ولا إجراء
// ممكن عندنا — نفس عائلة أخطاء IndexedDB الموثّقة في `src/firebase.js`.
//
// بدون هذا الفلتر كان الضجيج ≈88% من أحداث Sentry الأسبوعية — يخنق الأخطاء
// الحقيقية في التقرير ويستهلك الحصّة.
const NOISE = [
  /webkit\.messageHandlers/i,
  /Java object is gone/i,
  /Java exception was raised/i,
  /\bAbortError\b/,
  /The (?:user |)operation was aborted/i,
  /Database deleted by request of the user/i,
];

// يفحص رسالة/نوع الخطأ في كل المواضع التي قد يضعه فيها Sentry.
// مُصدَّرة كي يحرسها اختبار `__tests__/monitoringNoise.test.js`: الفلتر يبتلع
// أحداثاً، فأي توسيع فيه يجب أن يُثبَت أنه لا يبتلع خطأً حقيقياً.
export function isNoise(event, hint) {
  try {
    const err = hint && hint.originalException;
    const parts = [
      event && event.message,
      err && err.name,
      err && err.message,
    ];
    const values = (event && event.exception && event.exception.values) || [];
    for (const v of values) parts.push(v && v.type, v && v.value);
    return parts.some(p => typeof p === "string" && NOISE.some(rx => rx.test(p)));
  } catch {
    return false; // عند الشك: أرسِل الخطأ، لا تبتلعه
  }
}

// ── التقاط مبكّر: يغطّي النافذة بين إقلاع التطبيق ووصول حزمة Sentry ───────────
// يُفكّان فور الجاهزية كي لا يزدوج الالتقاط مع مستمعات Sentry العامة.
const _earlyErr = (e) => _queueError(e?.error || e?.message || "unknown error");
const _earlyRej = (e) => _queueError(e?.reason || "unhandled rejection");

function _queueError(err) {
  if (_pendingErrors.length < PENDING_MAX) _pendingErrors.push(err);
}

function _detachEarly() {
  try {
    window.removeEventListener("error", _earlyErr);
    window.removeEventListener("unhandledrejection", _earlyRej);
  } catch { /* no-op */ }
}

// تُستدعى مرة واحدة عند إقلاع التطبيق (src/index.js) قبل الـ render.
// **غير حاجبة**: تبدأ تحميل الحزمة وترجع فوراً — لا تؤخّر أول رسم إطلاقاً.
export function initMonitoring() {
  if (!DSN) return; // لا DSN → معطّل تماماً (بيئة تطوير/قبل الإعداد)، ولا تنزيل للحزمة
  try {
    window.addEventListener("error", _earlyErr);
    window.addEventListener("unhandledrejection", _earlyRej);
  } catch { /* no-op */ }

  // التحميل بعد خمول المتصفح لا فوراً: المراقبة ليست جزءاً من المسار الحرج، ولا
  // يجوز أن يزاحم جلبُها رسمَ المتجر على شبكة الجوال. المستمعان أعلاه يغطّيان
  // النافذة كاملةً فلا يضيع خطأ وقع أثناء الانتظار. المهلة سقف أمان لمتصفح لا
  // يصل إلى خمول أبداً (تبويب مزدحم).
  const _start = () => _loadSentry();
  try {
    if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(_start, { timeout: 4000 });
    else setTimeout(_start, 2000);
  } catch { _start(); }
}

function _loadSentry() {
  // ⚠️ نلتقط الصادرات المطلوبة بالتفكيك (destructuring) لا بحفظ فضاء الأسماء كاملاً
  // (`m => _S = m`): الأخير يجبر webpack على إبقاء **كل** الحزمة في الشنك (قِيست
  // 152KB مضغوطة)، بينما التفكيك يُبقي تحليل استعمال الصادرات شغّالاً فينكمش الشنك
  // إلى ما نستعمله فعلاً وحده.
  import("@sentry/react")
    .then(({ init, setUser, setTag, captureException }) => {
      init({
        dsn: DSN,
        environment: process.env.NODE_ENV || "production",
        // خفيف عمداً: التقاط الأخطاء فقط، بلا تتبّع أداء أو إعادة تشغيل جلسة
        // (لتوفير الحصة/التكلفة على خطة مالك المنصة). يمكن رفعها لاحقاً عند الحاجة.
        tracesSampleRate: 0,
        // لا نرسل الأخطاء أثناء التطوير المحلي
        enabled: process.env.NODE_ENV !== "development",
        // الفلترة كلها هنا (لا عبر ignoreErrors) حتى يبقى مصدر واحد للقرار،
        // ويستطيع captureError المتعمّد أن يمرّ دائماً بسياقه مهما كان نص الخطأ.
        beforeSend(event, hint) {
          if (event && event.tags && event.tags.manual === "1") return event;
          return isNoise(event, hint) ? null : event;
        },
      });
      _S = { setUser, setTag, captureException };
      _enabled = true;
      _detachEarly();
      // أفرغ ما تجمّع قبل الجاهزية: وسم المستخدم أولاً كي تحمله الأخطاء المُعادة.
      if (_pendingUser) { setMonitoringUser(_pendingUser); _pendingUser = null; }
      for (const err of _pendingErrors.splice(0)) {
        try { captureException(err); } catch { /* no-op */ }
      }
    })
    .catch((e) => {
      // تعذّر تحميل الحزمة (شبكة/حاجب إعلانات) — التطبيق يكمل بلا مراقبة كما لو
      // لم يُضبط DSN أصلاً. لا نُبقي طابوراً ينمو بلا مستهلك.
      _detachEarly();
      _pendingErrors.length = 0;
      _pendingUser = null;
      console.error("initMonitoring:", e);
    });
}

// يربط الجلسة الحالية بالشركة/الدور حتى تظهر الأخطاء مفلترة لمالك المنصة.
// تُستدعى عند الدخول واستعادة الجلسة.
export function setMonitoringUser(user) {
  if (!user) return;
  // لم تصل الحزمة بعد (الدخول/استعادة الجلسة يسبقانها عادةً) → احفظ الطلب وطبّقه
  // فور الجاهزية، وإلا ضاع وسم الشركة عن أخطاء أول دقيقة من الجلسة.
  if (!_enabled || !_S) { if (DSN) _pendingUser = user; return; }
  try {
    _S.setUser({
      id: user.id || user.companyId || undefined,
      username: user.name || undefined,
    });
    _S.setTag("companyId", user.companyId || "—");
    _S.setTag("role", user.role || "—");
  } catch { /* تجاهُل: المراقبة لا تكسر التطبيق */ }
}

// تُستدعى عند الخروج لمسح ربط الشركة بالأخطاء اللاحقة.
export function clearMonitoringUser() {
  _pendingUser = null;   // خروجٌ قبل جاهزية الحزمة يلغي وسماً مؤجّلاً (عزل)
  if (!_enabled || !_S) return;
  try { _S.setUser(null); } catch { /* no-op */ }
}

// التقاط خطأ يدوياً مع سياق إضافي (مثلاً من داخل catch لعملية مهمة).
// الوسم manual="1" يعفيه من فلتر الضجيج في beforeSend: ما دمنا اخترنا الإبلاغ
// عنه بأنفسنا ومعه سياق، فهو مقصود ويُرسَل مهما كان نص الخطأ.
export function captureError(err, context = {}) {
  if (!_enabled || !_S) {
    console.error("captureError:", err, context);
    // خطأ متعمّد وقع قبل جاهزية الحزمة: خزّنه ليُرسَل فور وصولها بدل أن يضيع.
    if (DSN) _queueError(err);
    return;
  }
  try { _S.captureException(err, { extra: context, tags: { manual: "1" } }); }
  catch { /* no-op */ }
}

// ── مكوّن حدود الأخطاء — يلتقط أعطال React ويعرض بديلاً بدل الشاشة البيضاء ────
// كان `Sentry.ErrorBoundary` مباشرةً، وهو ما ربط الحزمة بمسار الرندر فمنع تحميلها
// الكسول. البديل هنا صنف React عادي بنفس الواجهة المستعملة (fallback + children)،
// ويمرّر الخطأ لـ Sentry إن كانت جاهزة أو يخزّنه إن لم تصل بعد.
// السلوك المرئي مطابق تماماً: يعرض `fallback` عند العطل، ويعرض الأبناء بدونه —
// سواء ضُبط DSN أم لا (كما كان الحال قبل هذا التعديل).
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("App crash:", err, info);
    if (_enabled && _S) {
      try { _S.captureException(err, { extra: { componentStack: info?.componentStack } }); }
      catch { /* no-op */ }
    } else if (DSN) {
      _queueError(err);
    }
  }
  render() {
    return this.state.hasError ? (this.props.fallback ?? null) : this.props.children;
  }
}
